import { FormControlLabel, Stack, styled } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    PoolButton,
    PoolCheckbox,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolNumberTextfield,
    PoolTypography,
} from '@synergy/components-next';
import { useState } from 'react';

import { generateBonusSettlement } from '../store/bonus-settlements-overview-actions';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface GenerateBonusSettlementModalProps {
    open: boolean;
    onClose: () => void;
    /** Called once the settlement exists, so the list behind can fetch it. */
    onGenerated?: () => void;
}

const StyledCheckboxContainer = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: '4px',
    textAlign: 'left',
}));

export const GenerateBonusSettlementModal = (props: GenerateBonusSettlementModalProps) => {
    const { open, onClose, onGenerated } = props;
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [year, setYear] = useState<number | null>(null);
    const [isEmpty, setIsEmpty] = useState<boolean>(false);

    const handleSubmit = () => {
        if (!year) {
            return;
        }

        // The list behind this dialog is a page from the search, so it cannot
        // be told about the new settlement — it has to ask again. Without it
        // the settlement appeared only after a reload (#2211).
        void dispatch(generateBonusSettlement({ year, isEmpty })).then(result => {
            if (!('error' in result)) {
                onGenerated?.();
            }
        });

        onClose();
    };

    const handleClose = () => {
        setYear(null);
        setIsEmpty(false);
        onClose();
    };

    return (
        <PoolModal open={open} onClose={handleClose}>
            <PoolModalTitle>
                <FormattedMessage id="app.bonusSettlements.modal.generate" />
            </PoolModalTitle>
            <PoolModalContent>
                <Stack gap={2}>
                    <PoolNumberTextfield
                        label={intl.formatMessage({
                            id: 'app.common.year',
                        })}
                        value={year || ''}
                        onChange={e => setYear(+e.target.value)}
                        noFractionalNumbers={true}
                        hasThousandSeparator={false}
                        autoFocus={true}
                        noNegativeNumbers={true}
                    />
                    <StyledCheckboxContainer>
                        <FormControlLabel
                            control={<PoolCheckbox onChange={() => setIsEmpty(!isEmpty)} />}
                            label={
                                <PoolTypography
                                    typography="body2"
                                    translationId="app.bonusSettlements.modal.generateEmpty"
                                />
                            }
                            sx={{ m: 0 }}
                        />
                    </StyledCheckboxContainer>
                </Stack>
            </PoolModalContent>
            <PoolModalActions>
                <PoolButton variant="outlined" onClick={handleClose}>
                    <FormattedMessage id="app.common.close" />
                </PoolButton>
                <PoolButton onClick={handleSubmit}>
                    <FormattedMessage id="app.bonusSettlements.generate" />
                </PoolButton>
            </PoolModalActions>
        </PoolModal>
    );
};
